// Import React and necessary Material-UI components
import React from "react";
import "./Footer.css";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Link,
} from "@mui/material";

// Import Instagram icon from Material-UI
import { Instagram } from "@mui/icons-material";

// Import configuration for website images
import { websiteImages } from "../../../config/config";

// Import ArrowForwardIcon for navigation links
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Import useSelector hook for accessing Redux store
import { useSelector } from "react-redux";

// Define the Footer component
const Footer = () => {
  // Retrieve support email and phone from Redux global settings
  const { support_email, support_phone } = useSelector(
    (state) => state.globalSettings
  );

  return (
    <div className="footer-container">
      <footer>
        {/************************** Upper Portion: Newsletter Section **************************/}
        <div className="fot-part-1">
          <Box className="newsletter-section container">
            <Grid
              container
              spacing={3}
              alignItems="center"
              justifyContent="space-between"
            >
              {/* Left: Newsletter Title and Subtitle */}
              <Grid item xs={12} sm={12} md={4}>
                <Box className="left-section">
                  <Typography className="subscribe-label">
                    SUBSCRIBE TO OUR NEWS
                  </Typography>
                  <Typography className="subscribe-heading">
                    Stay in Touch
                  </Typography>
                </Box>
              </Grid>

              {/* Middle: Email Input and Subscribe Button */}
              <Grid item xs={12} sm={12} md={5}>
                <Box className="middle-section">
                  <Box className="subscribe-form">
                    <TextField
                      placeholder="Your email ..."
                      variant="outlined"
                      size="small"
                      className="email-input"
                      fullWidth
                      required
                    />
                    <Button className="subscribe-button">SUBSCRIBE NOW</Button>
                  </Box>
                </Box>
              </Grid>

              {/* Right: Social Media Icons */}
              <Grid item xs={12} sm={12} md={3}>
                <Box className="right-section">
                  <Box className="social-icons">
                    {/* Facebook Icon */}
                    <IconButton className="icon-border">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 48 48"
                      >
                        <path
                          fill="#00ffff"
                          stroke="#000"
                          strokeLinejoin="round"
                          strokeWidth="3.8"
                          d="M36 12.5997H31.2489H29.9871C28.9009 12.5997 28.0203 13.4803 28.0203 14.5666V21.4674H36L34.8313 29.0643H28.0203V43H19.2451V29.0643H12V21.4674H19.1515L19.2451 14.2563L19.2318 12.9471C19.1879 8.60218 22.6745 5.04434 27.0194 5.0004C27.0459 5.00013 27.0724 5 27.0989 5H36V12.5997Z"
                        />
                      </svg>
                    </IconButton>
                    {/* Instagram Icon */}
                    <IconButton className="icon-border">
                      <Instagram sx={{ color: "#00ffff" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </div>

        {/************************** Middle Portion: Footer Content **************************/}
        <div className="fot-part-2">
          <Box className="container">
            <Grid container spacing={4} className="footer-content">
              {/* Left: Logo and Description */}
              <Grid item xs={12} md={5}>
                <Box className="footer-left">
                  <Box
                    className="footer-logo"
                    component="img"
                    src={websiteImages.websiteLogo}
                    alt="RECOPM+"
                    sx={{ height: "30px", width: "130px" }}
                  />
                  <Typography className="footer-description">
                    Forget the guesswork. Forget the rebound. This is a
                    structured, coach-led system that builds muscle, burns fat,
                    and fits your life — for good.
                  </Typography>
                </Box>
              </Grid>

              {/* Middle: Navigation Links */}
              <Grid item xs={12} md={4}>
                <Box className="footer-middle nav-menu-1">
                  <Typography className="footer-section-title">
                    Explore
                  </Typography>
                  <Grid container spacing={10}>
                    <Grid item xs={6}>
                      <Box className="footer-links">
                        <Link href="#" className="footer-link">
                          <ArrowForwardIcon
                            fontSize="small"
                            sx={{ color: "#00ffff" }}
                            className="nav-arrow"
                          />{" "}
                          Articles
                        </Link>
                        <Link href="#" className="footer-link">
                          <ArrowForwardIcon
                            fontSize="small"
                            sx={{ color: "#00ffff" }}
                            className="nav-arrow"
                          />{" "}
                          FAQ
                        </Link>
                        <Link href="#" className="footer-link">
                          <ArrowForwardIcon
                            fontSize="small"
                            sx={{ color: "#00ffff" }}
                            className="nav-arrow"
                          />{" "}
                          Contacts
                        </Link>
                        <Link href="#" className="footer-link">
                          <ArrowForwardIcon
                            fontSize="small"
                            sx={{ color: "#00ffff" }}
                            className="nav-arrow"
                          />{" "}
                          Testimonials
                        </Link>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box className="footer-links">
                        <Link href="#" className="footer-link">
                          <ArrowForwardIcon
                            fontSize="small"
                            sx={{ color: "#00ffff" }}
                            className="nav-arrow"
                          />{" "}
                          About Us
                        </Link>
                        <Link href="#" className="footer-link">
                          <ArrowForwardIcon
                            fontSize="small"
                            sx={{ color: "#00ffff" }}
                            className="nav-arrow"
                          />{" "}
                          Blog
                        </Link>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Right: Contact Information */}
              <Grid item xs={12} md={3}>
                <Box className="footer-right">
                  {/* Address Section */}
                  <Box className="contact-item">
                    <Box
                      component="img"
                      src={websiteImages.addressIcon}
                      alt="Address Icon"
                      sx={{ height: "28px", width: "28px" }}
                    />
                    <Box>
                      <Typography className="contact-label">
                        Address:
                      </Typography>
                      <Typography className="contact-value">
                        903/50 Clarence St, Sydney NSW 2000 <br /> (9th level of
                        building 50)
                      </Typography>
                    </Box>
                  </Box>

                  {/* Email Section */}
                  <Box className="contact-item">
                    <Box
                      className="email-icon"
                      component="img"
                      src={websiteImages.emailIcon}
                      alt="Email Icon"
                      sx={{ height: "28px", width: "28px" }}
                    />
                    <Box>
                      <Typography className="contact-label">Email:</Typography>
                      <Typography className="contact-value">
                        {support_email || "support@example.com"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Phone Section */}
                  <Box className="contact-item">
                    <Box
                      component="img"
                      src={websiteImages.callIcon}
                      alt="Phone Icon"
                      sx={{ height: "28px", width: "28px" }}
                    />
                    <Box>
                      <Typography className="contact-label">Phone:</Typography>
                      <Typography className="contact-value">
                        {support_phone || "0-900-850-05-59"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Working Hours Section */}
                  <Box className="contact-item">
                    <Box
                      component="img"
                      src={websiteImages.timeIcon}
                      alt="Time Icon"
                      sx={{ height: "28px", width: "28px" }}
                    />
                    <Box>
                      <Typography className="contact-label">
                        Working Hours:
                      </Typography>
                      <Typography className="contact-value">
                        Mon-Fri: 8am - 4pm
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </div>

        {/************************** Copyright Portion **************************/}
        <div className="fot-part-3">
          <Box className="container">
            Copyright ©2025{" "}
            <span className="copy-text"> Coach Paul Gradwell</span>
          </Box>
        </div>
      </footer>
    </div>
  );
};

// Export the Footer component
export default Footer;